import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Truck } from "lucide-react";

export default function ShippingTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [pz, setPz] = useState("");
  const [psub, setPsub] = useState("100");
  const [preview, setPreview] = useState(null);
  const [pErr, setPErr] = useState("");
  const runPreview = async () => {
    setPErr(""); setPreview(null);
    try {
      const params = { subtotal: parseFloat(psub) || 0 };
      const v = pz.trim();
      if (/^\d{5}$/.test(v)) params.zip = v; else if (v) params.state = v;
      const { data } = await api.get("/farm/shipping/preview", { params });
      setPreview(data);
    } catch (e) { setPErr(e.response?.data?.detail || "Preview failed"); }
  };

  const hydrate = (d) => ({
    ...d,
    free_shipping_over: d.free_shipping_over ?? "",
    shipping_zones: (d.shipping_zones || []).map((z) => ({ ...z, statesText: (z.states || []).join(", ") })),
  });

  useEffect(() => { api.get("/farm/shipping").then((r) => setCfg(hydrate(r.data))).catch(() => {}); }, []);
  if (!cfg) return <p className="text-sm text-[var(--ff-text-muted)]">Loading shipping…</p>;

  const addZone = () => setCfg({ ...cfg, shipping_zones: [...cfg.shipping_zones, { name: "", statesText: "", rate: 0 }] });
  const rmZone = (i) => setCfg({ ...cfg, shipping_zones: cfg.shipping_zones.filter((_, idx) => idx !== i) });
  const setZone = (i, k, v) => setCfg({ ...cfg, shipping_zones: cfg.shipping_zones.map((z, idx) => (idx === i ? { ...z, [k]: v } : z)) });

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {
        shipping_zones: cfg.shipping_zones.filter((z) => (z.name || "").trim()).map((z) => ({
          name: z.name.trim(),
          states: (z.statesText || "").split(",").map((s) => s.trim()).filter(Boolean),
          rate: parseFloat(z.rate) || 0,
        })),
        default_shipping_rate: parseFloat(cfg.default_shipping_rate) || 0,
        free_shipping_over: cfg.free_shipping_over === "" || cfg.free_shipping_over == null ? null : parseFloat(cfg.free_shipping_over),
        ships_to_regions: cfg.ships_to_regions || "",
      };
      const { data } = await api.put("/farm/shipping", payload);
      setCfg(hydrate(data));
      setMsg("Shipping saved."); setTimeout(() => setMsg(""), 2500);
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="farm-shipping-tab">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[var(--ff-primary)] text-white flex items-center justify-center"><Truck size={18} /></div>
        <div>
          <h3 className="font-display text-2xl">Shipping rates &amp; zones</h3>
          <p className="text-sm text-[var(--ff-text-muted)]">Set a flat rate per region. Non-members pay the rate for their destination state at checkout; members always ship free. States you don&apos;t list fall back to your default rate.</p>
        </div>
      </div>

      {err && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="shipping-error">{err}</div>}
      {msg && <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-sm text-emerald-800" data-testid="shipping-success">{msg}</div>}

      <div className="card-flat p-5 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="overline">Default flat rate (USD)</span>
          <input type="number" step="0.01" min="0" className="input-base mt-2" value={cfg.default_shipping_rate} onChange={(e) => setCfg({ ...cfg, default_shipping_rate: e.target.value })} data-testid="shipping-default-rate" />
        </label>
        <label className="block">
          <span className="overline">Free shipping over (USD, optional)</span>
          <input type="number" step="0.01" min="0" placeholder="e.g. 150 — leave blank to disable" className="input-base mt-2" value={cfg.free_shipping_over} onChange={(e) => setCfg({ ...cfg, free_shipping_over: e.target.value })} data-testid="shipping-free-over" />
        </label>
        <label className="block sm:col-span-2">
          <span className="overline">Ships to (shown on your storefront)</span>
          <input className="input-base mt-2" placeholder="Lower 48 states" value={cfg.ships_to_regions || ""} onChange={(e) => setCfg({ ...cfg, ships_to_regions: e.target.value })} data-testid="shipping-ships-to" />
        </label>
      </div>

      <div className="card-flat p-5 space-y-3" data-testid="shipping-zones">
        <div className="flex items-center justify-between">
          <p className="overline">Regional zones</p>
          <button onClick={addZone} className="btn-secondary text-xs" data-testid="shipping-add-zone"><Plus size={14} /> Add zone</button>
        </div>
        {cfg.shipping_zones.length === 0 && <p className="text-sm text-[var(--ff-text-muted)]">No custom zones yet — every order uses your default rate.</p>}
        {cfg.shipping_zones.map((z, i) => (
          <div key={i} className="grid sm:grid-cols-[1.2fr_1.6fr_0.8fr_auto] gap-3 items-end border-t border-[var(--ff-border)] pt-3" data-testid={`shipping-zone-${i}`}>
            <label className="block"><span className="overline">Zone name</span>
              <input className="input-base mt-1" placeholder="Regional" value={z.name} onChange={(e) => setZone(i, "name", e.target.value)} data-testid={`shipping-zone-name-${i}`} /></label>
            <label className="block"><span className="overline">States (comma-separated)</span>
              <input className="input-base mt-1" placeholder="TX, OK, NM  or  ALL" value={z.statesText} onChange={(e) => setZone(i, "statesText", e.target.value)} data-testid={`shipping-zone-states-${i}`} /></label>
            <label className="block"><span className="overline">Rate $</span>
              <input type="number" step="0.01" min="0" className="input-base mt-1" value={z.rate} onChange={(e) => setZone(i, "rate", e.target.value)} data-testid={`shipping-zone-rate-${i}`} /></label>
            <button onClick={() => rmZone(i)} className="p-2 text-rose-600 hover:bg-rose-50 rounded mb-1" data-testid={`shipping-zone-remove-${i}`}><Trash2 size={16} /></button>
          </div>
        ))}
        <p className="text-xs text-[var(--ff-text-muted)]">Tip: use <strong>ALL</strong> in a zone&apos;s states to apply one rate everywhere. Use two-letter state codes.</p>
      </div>

      <div className="card-flat p-5" data-testid="shipping-preview">
        <p className="overline mb-1">Zone rate preview</p>
        <p className="text-xs text-[var(--ff-text-muted)] mb-3">Enter a customer ZIP (or 2-letter state) to see what a non-member there would be charged. Save your zones first for accurate results.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block"><span className="overline">Destination ZIP or state</span>
            <input className="input-base mt-1" placeholder="78701 or TX" value={pz} onChange={(e) => setPz(e.target.value)} data-testid="shipping-preview-zip" /></label>
          <label className="block"><span className="overline">Order subtotal $</span>
            <input type="number" className="input-base mt-1 w-32" value={psub} onChange={(e) => setPsub(e.target.value)} data-testid="shipping-preview-subtotal" /></label>
          <button onClick={runPreview} className="btn-secondary" data-testid="shipping-preview-btn">Preview rate</button>
        </div>
        {pErr && <p className="text-sm text-rose-700 mt-2">{pErr}</p>}
        {preview && (
          <div className="mt-3 text-sm bg-[var(--ff-muted)] rounded-lg p-3" data-testid="shipping-preview-result">
            {preview.state
              ? <>To <strong>{preview.city ? preview.city + ", " : ""}{preview.state}</strong>: a non-member pays <strong className="text-[var(--ff-primary)]">${preview.rate.toFixed(2)}</strong>{preview.free_over_applied ? " (your free-shipping threshold was met)" : ""} · members always ship free.</>
              : "Couldn't resolve that destination — try a valid 5-digit ZIP or 2-letter state code."}
          </div>
        )}
      </div>

      <button onClick={save} disabled={busy} className="btn-primary" data-testid="shipping-save-btn">{busy ? "Saving…" : "Save shipping"}</button>
    </div>
  );
}
